import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Country, State } from "country-state-city";
import { toast } from "react-toastify";
import axios from "axios";
import API_URL from "../common/config2";

export default function Impact() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    startupName: "",
    website: "",
    elevatorPitch: "",
    country: "",
    state: "",
    city: "",
    incubationProgram: "",
    whatsapp: "",
    countryCode: "+91",
  });
  const [stateOptions, setStateOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  useEffect(() => {
    if (!formData.country) {
      setStateOptions([]);
      setFormData((prev) => ({ ...prev, countryCode: "", state: "" }));
      return;
    }
    const selectedCountry = allCountries.find((c) => c.name === formData.country);
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry.isoCode);
      setStateOptions(states);
      setFormData((prev) => ({ ...prev, countryCode: `+${selectedCountry.phonecode}` }));
    }
  }, [formData.country, allCountries]);

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const fullPhone = `${formData.countryCode}${formData.whatsapp}`;
    const numericPhone = fullPhone.replace(/\D/g, "");
    if (numericPhone.length < 8 || numericPhone.length > 15) {
      toast.error("Please enter a valid WhatsApp number.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/impact/register`, formData, {
        headers: { "Content-Type": "application/json" }
      });

      if (response.data.success) {
        toast.success("Application submitted successfully! Please check your email.");
        setFormData({
          fullName: "",
          email: "",
          startupName: "",
          website: "",
          elevatorPitch: "",
          country: "",
          state: "",
          city: "",
          incubationProgram: "",
          whatsapp: "",
          countryCode: "+91",
        });
      } else {
        toast.error("Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("An error occurred while submitting. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div>
        <section
          className="page-hero d-block d-lg-flex align-items-lg-center"
          id="page-hero"
          style={{
            minHeight: "100vh",
            position: "relative",
            overflowX: "hidden",
            paddingTop: "100px",
            paddingBottom: "40px"
          }}
        >
          {/* Background Layer */}
          <div
            className="overlay-photo-image-bg"
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: 'url("./assets/images/sections-bg-images/pattern-bg-2.jpg")',
              opacity: 0.2,
              zIndex: -1,
            }}
          />
          <div
            className="particles-js bubels"
            id="particles-js"
            style={{ position: "absolute", inset: 0, zIndex: 0 }}
          />

          <div className="container px-3 px-md-4 py-3" style={{ zIndex: 1 }}>
            <div className="row align-items-center gy-5">
              {/* Text Content */}
              <div className="col-12 col-lg-6 text-center text-lg-start">
                <div className="hero-text-area px-2 px-md-0">
                  <h1 className="fw-bold mb-3 position-relative" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", lineHeight: "1.2" }}>
                    IMPACT <br />
                    Innovate Without <span style={{ color: "#7c3aed" }}>Borders.</span>
                    <span className="design-element rounded-shape stripes d-none d-md-inline-block" />
                  </h1>

                  <p
                    className="hero-subtitle mb-4 mx-auto mx-lg-0 text-white opacity-75"
                    style={{
                      maxWidth: "500px",
                      fontSize: "clamp(1rem, 2vw, 1.1rem)",
                      lineHeight: "1.5",
                    }}
                  >
                    Empowering grassroots innovators around the world and helping them create a venture.
                    <br /><br />
                    You have an idea. You have a solution to a problem. You want to build something meaningful.
                    IMPACT gives you access to structured virtual incubation, expert guidance and a global innovation community - wherever you are.
                  </p>

                  <div className="mb-4">
                    <h3 className="fw-bold text-white mb-1" style={{ color: "#7c3aed" }}>Just $5/month</h3>
                    <p className="text-white opacity-75" style={{ fontSize: "1rem" }}>Affordable incubation for innovators everywhere.</p>
                  </div>
                </div>
              </div>

              {/* Form Area */}
              <div className="col-12 col-lg-6 d-flex justify-content-center align-items-center">
                <div
                  className="hero-form-wrapper position-relative"
                  style={{ maxWidth: "680px", width: "100%", zIndex: 2 }}
                >
                  {/* The Form Card */}
                  <div
                    className="p-2 p-md-4 p-lg-5"
                    style={{
                      backgroundColor: "#131122",
                      borderRadius: "24px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      boxShadow: "0 40px 80px rgba(0, 0, 0, 0.4)",
                      color: "#ffffff",
                    }}
                  >
                    <div className="text-center mb-2">
                      <h4 className="fw-bold text-white mb-1">Join iMPACT++</h4>
                    </div>

                    <form onSubmit={handleFormSubmit}>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>FULL NAME *</label>
                          <input type="text" name="fullName" className="form-control bg-transparent text-white py-2" placeholder="Jane Doe" value={formData.fullName} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>EMAIL ADDRESS *</label>
                          <input type="email" name="email" className="form-control bg-transparent text-white py-2" placeholder="jane@example.com" value={formData.email} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>STARTUP NAME *</label>
                          <input type="text" name="startupName" className="form-control bg-transparent text-white py-2" placeholder="Acme Corp" value={formData.startupName} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>WEBSITE LINK (OPTIONAL)</label>
                          <input type="url" name="website" className="form-control bg-transparent text-white py-2" placeholder="https://example.com" value={formData.website} onChange={handleFormChange} style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>COUNTRY *</label>
                          <select name="country" className="form-select bg-transparent text-white py-2" value={formData.country} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(30,20,60,0.95)" }}>
                            <option value="" className="text-dark">Select Country</option>
                            {allCountries.map((c) => (<option key={c.name} value={c.name} className="text-dark">{c.name}</option>))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>STATE / PROVINCE *</label>
                          <select name="state" className="form-select bg-transparent text-white py-1" value={formData.state} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(30,20,60,0.95)" }}>
                            <option value="" className="text-dark">Select State</option>
                            {stateOptions.map((s) => (<option key={s.name} value={s.name} className="text-dark">{s.name}</option>))}
                          </select>
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>CITY *</label>
                          <input type="text" name="city" className="form-control bg-transparent text-white py-2" placeholder="Your City" value={formData.city} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                        <div className="col-md-6" >
                          <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>REGIONAL PROGRAM</label>
                          <select name="incubationProgram" className="form-select bg-transparent text-white py-1" value={formData.incubationProgram} onChange={handleFormChange} style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(30,20,60,0.95)" }}>
                            <option value="" className="text-dark">Select available program (Optional)</option>
                            <option value="Mohali" className="text-dark">Mohali</option>
                            <option value="Nagpur" className="text-dark">Nagpur</option>
                            <option value="Princeton" className="text-dark">Princeton</option>
                            <option value="Raipur" className="text-dark">Raipur</option>
                          </select>
                        </div>
                      </div>



                      <div className="mb-3">
                        <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>WHATSAPP NUMBER *</label>
                        <div className="d-flex gap-2">
                          <div className="d-flex align-items-center justify-content-center fw-bold px-3" style={{ minWidth: "70px", color: "#fff", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(255,255,255,0.03)" }}>
                            {formData.countryCode || "+91"}
                          </div>
                          <input type="tel" name="whatsapp" className="form-control bg-transparent text-white py-2 flex-grow-1" placeholder="98765 43210" value={formData.whatsapp} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)" }} />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label text-white opacity-50 fw-semibold mb-1" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>ELEVATOR PITCH *</label>
                        <textarea name="elevatorPitch" rows="1" className="form-control bg-transparent text-white py-2" placeholder="Describe your idea or startup briefly..." value={formData.elevatorPitch} onChange={handleFormChange} required style={{ borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", resize: "none" }}></textarea>
                      </div>



                      <button type="submit" disabled={isSubmitting} className="btn btn-primary w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 mt-2" style={{ backgroundColor: "#7c3aed", border: "none", borderRadius: "12px", fontSize: "1.05rem", boxShadow: "0 10px 20px rgba(124, 58, 237, 0.3)" }}>
                        {isSubmitting ? "SUBMITTING..." : <>APPLY FOR IMPACT <ArrowRight size={18} /></>}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Sections - Matching Elevate Theme */}
        <section className="about-section py-5" style={{ backgroundColor: "#0b0a1a" }}>
          <div className="container px-3 px-md-4 py-4 text-white">
            <div className="row gy-5">
              <div className="col-12 col-lg-6">
                <div className="pe-lg-5 text-center text-lg-start px-2 px-md-0">
                  <h2 className="fw-bold mb-4" style={{ color: "#7c3aed" }}>What is IMPACT?</h2>
                  <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                    IMPACT is a virtual incubation program by IM Global, created to make quality incubation accessible to grassroots innovators across the world.
                  </p>
                  <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                    Whether you are a student, farmer, artisan, community innovator, first-time entrepreneur or an aspiring founder, IMPACT helps you transform your idea into a viable venture.
                  </p>
                  <div className="d-inline-block py-2 px-4 mt-3 rounded-pill text-uppercase fw-bold" style={{ backgroundColor: "rgba(124, 58, 237, 0.1)", color: "#7c3aed", border: "1px solid #7c3aed" }}>
                    IDEA → VALIDATION → VENTURE
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-6 px-3 px-md-2">
                <div className="p-4 rounded-4" style={{ backgroundColor: "#131122", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <h3 className="fw-bold mb-4">Who Can Join?</h3>
                  <p className="opacity-75 mb-4">IMPACT is for grassroots innovators who:</p>
                  <ul className="list-unstyled opacity-75" style={{ lineHeight: "1.8" }}>

                    <li className="mb-2 d-flex align-items-start"><span className="me-2" style={{ color: "#7c3aed" }}>•</span> Have an early-stage business idea</li>
                    <li className="mb-2 d-flex align-items-start"><span className="me-2" style={{ color: "#7c3aed" }}>•</span> Have identified a problem they want to solve</li>


                    <li className="mb-2 d-flex align-items-start"><span className="me-2" style={{ color: "#7c3aed" }}>•</span> Want to turn their skills or ideas into a venture</li>
                    <li className="mb-2 d-flex align-items-start"><span className="me-2" style={{ color: "#7c3aed" }}>•</span> Are students or first-time entrepreneurs</li>
                    <li className="mb-2 d-flex align-items-start"><span className="me-2" style={{ color: "#7c3aed" }}>•</span> Come from rural, underserved or emerging ecosystems</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="row mt-5 pt-4">
              <div className="col-12 text-center mb-5">
                <h2 className="fw-bold">What You Get</h2>
              </div>

              {[
                { title: "Venture Building", desc: "A structured pathway to turn your idea into a business.", icon: "🚀" },
                { title: "Expert Mentorship", desc: "Guidance from entrepreneurs, mentors and industry experts.", icon: "🧭" },
                { title: "Idea & Business Validation", desc: "Test your problem, solution, customer and business model.", icon: "💡" },
                { title: "Market Readiness", desc: "Work on pricing, sales, marketing, customer acquisition and growth.", icon: "📈" },
                { title: "Global Opportunities", desc: "Access a global network of innovators, mentors, partners and opportunities.", icon: "🌍" },
                { title: "Community", desc: "Become part of a growing community of grassroots innovators from around the world.", icon: "🤝" }
              ].map((item, idx) => (
                <div key={idx} className="col-12 col-md-6 col-lg-4 mb-4 px-3 px-md-2">
                  <div className="p-4 rounded-4 h-100" style={{ backgroundColor: "#131122", border: "1px solid rgba(255,255,255,0.05)", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
                    <div className="mb-3" style={{ fontSize: "2rem" }}>{item.icon}</div>
                    <h4 className="fw-bold mb-3">{item.title}</h4>
                    <p className="opacity-75 mb-0" style={{ fontSize: "0.95rem" }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>



          </div>
        </section>
      </div>
    </>
  );
}
